import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Package,
  Tag,
  Weight,
  DollarSign,
  Globe,
  Loader2,
  X,
  Save,
  AlertCircle,
  Info,
  Sparkles,
  CheckCircle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { MiscItem } from '../types';
import { WeightUnit } from '../utils/weightConversion';
import { 
  createMiscItem, 
  updateMiscItem, 
  validateMiscItem 
} from '../services/miscItemsService';

interface MiscItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item?: MiscItem | null;
  mode: 'add' | 'edit';
}

export function MiscItemModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  item, 
  mode 
}: MiscItemModalProps) {
  const [formData, setFormData] = useState<Partial<MiscItem>>({
    name: '',
    description: '',
    weight: 0,
    weightUnit: 'oz',
    value: 0,
    hsCode: '',
    originCountry: 'US',
    manufacturer: '',
    isActive: true,
    category: '',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or item changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && item) {
        setFormData({
          name: item.name || '',
          description: item.description || '',
          weight: item.weight || 0,
          weightUnit: item.weightUnit || 'oz',
          value: item.value || 0,
          hsCode: item.hsCode || '',
          originCountry: item.originCountry || 'US',
          manufacturer: item.manufacturer || '',
          isActive: item.isActive !== undefined ? item.isActive : true,
          category: item.category || '',
        });
      } else {
        setFormData({
          name: '',
          description: '',
          weight: 0,
          weightUnit: 'oz',
          value: 0,
          hsCode: '',
          originCountry: 'US',
          manufacturer: '',
          isActive: true,
          category: '',
        });
      }
      setErrors([]);
    }
  }, [isOpen, item, mode]);

  const handleInputChange = (field: keyof MiscItem, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    const validationErrors = validateMiscItem(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (mode === 'edit' && item?.id) {
        await updateMiscItem(item.id, formData);
        toast.success('Misc item updated successfully');
      } else {
        await createMiscItem(formData as Omit<MiscItem, 'id' | 'createdAt' | 'updatedAt'>);
        toast.success('Misc item created successfully');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving misc item:', error);
      toast.error('Failed to save misc item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const weightUnits: { value: WeightUnit; label: string }[] = [
    { value: 'g', label: 'Grams (g)' },
    { value: 'kg', label: 'Kilograms (kg)' },
    { value: 'oz', label: 'Ounces (oz)' },
    { value: 'lb', label: 'Pounds (lb)' },
  ];

  const categories = [
    { value: 'cards', label: 'Cards & Notes' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'samples', label: 'Samples' },
    { value: 'packaging', label: 'Packaging Materials' },
    { value: 'promotional', label: 'Promotional Items' },
  ];

  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CN', name: 'China' },
  ];

  const quickTemplates = [
    {
      name: 'Thank You Card',
      description: 'Personalized thank you card with JamPack\'d branding',
      weight: 1,
      weightUnit: 'oz' as WeightUnit,
      value: 0.50,
      hsCode: '490199',
      category: 'cards',
    },
    {
      name: 'Recipe Card',
      description: 'Traditional Jamaican recipe card',
      weight: 0.5,
      weightUnit: 'oz' as WeightUnit,
      value: 0.30,
      hsCode: '490199',
      category: 'cards',
    },
    {
      name: 'Product Info Leaflet',
      description: 'Information about product origins and usage',
      weight: 0.3,
      weightUnit: 'oz' as WeightUnit,
      value: 0.10,
      hsCode: '490199',
      category: 'documentation',
    },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-emerald-500 to-teal-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <Package className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    {mode === 'edit' ? 'Edit Misc Item' : 'Add Misc Item'}
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Configure additional items for shipping labels
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Form */}
          <div className="p-8 overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            <form id="misc-item-form" onSubmit={handleSubmit} className="space-y-8">
              {/* Error Display */}
              <AnimatePresence>
                {errors.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-red-800 mb-1">Please fix the following errors:</h4>
                        <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                          {errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Quick Templates */}
              {mode === 'add' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100"
                >
                  <h4 className="text-lg font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    Quick Templates
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {quickTemplates.map((template) => (
                      <button
                        key={template.name}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            ...template,
                            originCountry: 'US',
                            manufacturer: 'JamPack\'d',
                          }));
                        }}
                        className="text-left p-4 bg-white border border-emerald-200 rounded-xl text-sm hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200 hover:shadow-md"
                      >
                        <div className="font-semibold text-emerald-800">{template.name}</div>
                        <div className="text-emerald-600 text-xs mt-1">${template.value.toFixed(2)}</div>
                        <div className="text-gray-600 text-xs mt-2">{template.description}</div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Basic Information */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Tag className="h-5 w-5 text-emerald-500" />
                  Item Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="name" className="text-base font-semibold text-gray-900">Item Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g., Thank You Card"
                      className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="category" className="text-base font-semibold text-gray-900">Category</Label>
                    <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                      <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md transition-all duration-200">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="z-[10002]">
                        {categories.map((category) => (
                          <SelectItem key={category.value} value={category.value}>
                            {category.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="description" className="text-base font-semibold text-gray-900">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Detailed description for customs declarations"
                    rows={3}
                    className="rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                  />
                  <p className="text-sm text-gray-600">
                    This description will appear on customs forms for international shipments.
                  </p>
                </div>
              </motion.div>

              {/* Physical Properties */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Weight className="h-5 w-5 text-emerald-500" />
                  Physical Properties
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="weight" className="text-base font-semibold text-gray-900">Weight *</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.weight || ''}
                      onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || 0)}
                      placeholder="1.0"
                      className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="weightUnit" className="text-base font-semibold text-gray-900">Weight Unit *</Label>
                    <Select value={formData.weightUnit} onValueChange={(value) => handleInputChange('weightUnit', value)}>
                      <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md transition-all duration-200">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent className="z-[10002]">
                        {weightUnits.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="value" className="text-base font-semibold text-gray-900">Value (USD) *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                    <Input
                      id="value"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.value || ''}
                      onChange={(e) => handleInputChange('value', parseFloat(e.target.value) || 0)}
                      placeholder="0.50"
                      className="pl-12 h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Declared value for customs purposes.
                  </p>
                </div>
              </motion.div>

              {/* Customs Information */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-emerald-500" />
                  Customs Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="originCountry" className="text-base font-semibold text-gray-900">Origin Country *</Label>
                    <Select value={formData.originCountry} onValueChange={(value) => handleInputChange('originCountry', value)}>
                      <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md transition-all duration-200">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="z-[10002]">
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name} ({country.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="manufacturer" className="text-base font-semibold text-gray-900">Manufacturer</Label>
                    <Input
                      id="manufacturer"
                      value={formData.manufacturer}
                      onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                      placeholder="JamPack'd"
                      className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="hsCode" className="text-base font-semibold text-gray-900">HS Code</Label>
                  <Input
                    id="hsCode"
                    value={formData.hsCode}
                    onChange={(e) => handleInputChange('hsCode', e.target.value)}
                    placeholder="490199"
                    className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                  />
                  <p className="text-sm text-gray-600">
                    6-10 digit Harmonized System code for international trade classification.
                    <a 
                      href="https://hts.usitc.gov/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 hover:text-emerald-700 ml-1 font-medium"
                    >
                      Look up HS codes here
                    </a>
                  </p>
                </div>
              </motion.div>

              {/* Status */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <label className="text-base font-semibold text-gray-900">Active</label>
                      <p className="text-sm text-gray-600 mt-1">
                        Active items are available for selection during label generation
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={(checked) => handleInputChange('isActive', checked)}
                  />
                </div>
              </motion.div>
            </form>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  type="submit"
                  form="misc-item-form"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-emerald-500/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {mode === 'edit' ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {mode === 'edit' ? 'Update Item' : 'Create Item'}
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}